import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
} from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import { UserChip } from "components/lib/Chips";

const PersistentToken = () => {
  const reloadUser = useUserReload();
  const [persistentToken, setPersistentToken] = useState<
    | OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"]
    | null
  >(null);
  const [persistentTokenLoading, setPersistentTokenLoading] = useState(false);
  const [persistentTokenErrorMessage, setPersistentTokenErrorMessage] =
    useState<string>("");
  const { t } = useTranslation();

  const [redeemingPersistentToken, setRedeemingPersistentToken] =
    useState(false);

  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const { token } = useParams<{ token: string }>();

  const getEntityAction = (
    persistentToken: OpenAPI.Schemas["PersistentToken"],
  ) => {
    const { forEntity } = persistentToken;
    if (forEntity === "invitation") {
      return t("generic.invited");
    }
    return "";
  };

  const getForTypeText = (
    persistentToken: OpenAPI.Schemas["PersistentToken"],
  ) => {
    const { forType } = persistentToken;
    if (forType === "ideaBusinessProfile") {
      return t("persistentToken.joinBusinessProfile");
    } else if (forType === "idea") {
      return t("persistentToken.joinIdea");
    } else if (forType === "group") {
      return t("persistentToken.joinGroup");
    }
    return "";
  };

  const getEntityName = (
    persistentToken: OpenAPI.Schemas["PersistentToken"],
  ) => {
    const { forType } = persistentToken;
    if (forType === "ideaBusinessProfile") {
      const { ownerIdeaBusinessProfile } = persistentToken;
      return ownerIdeaBusinessProfile?.name ?? t("generic.unknown");
    } else if (forType === "idea") {
      const { ownerIdea } = persistentToken;
      return ownerIdea?.name ?? t("generic.unknown");
    } else if (forType === "group") {
      const { ownerGroup } = persistentToken;
      return ownerGroup?.name ?? t("generic.unknown");
    }
    return "";
  };

  const getActionText = (
    persistentToken: OpenAPI.Schemas["PersistentToken"],
  ) => {
    const { forEntity } = persistentToken;
    if (forEntity === "invitation") {
      return t("persistentToken.acceptInvitation");
    }
    return "";
  };

  // If we have data indicating the user has previously visited this link, and created an entity/accepted an invitation, redirect them to the entity
  const handleRedirect = useCallback(
    (
      persistentToken: OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"],
    ) => {
      if (persistentToken.forEntity === "invitation") {
        if (persistentToken.forType === "ideaBusinessProfile") {
          navigate(`/?businessProfileId=${persistentToken.forId}`); // See AutoBusinessProfileModal.tsx
        } else if (persistentToken.forType === "idea") {
          navigate(`/ideas/${persistentToken.forId}`);
        } else if (persistentToken.forType === "group") {
          navigate(`/groups/${persistentToken.forId}`);
        }
      }
    },
    [navigate],
  );

  const getPersistentToken = useCallback(() => {
    if (!token) return;

    setPersistentTokenLoading(true);
    setPersistentTokenErrorMessage("");

    api.persistentTokens.getByToken(
      token,
      (res: OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]) => {
        if (res.userHasRedeemedToken) {
          handleRedirect(res.persistentToken);
        }
        setPersistentToken(res.persistentToken);
        setPersistentTokenLoading(false);
      },
      (err) => {
        setPersistentTokenErrorMessage(err.message);
        setPersistentTokenLoading(false);
      },
    );
  }, [handleRedirect, token]);

  useEffect(() => {
    getPersistentToken();
  }, [getPersistentToken]);

  const redeemPersistentToken = useCallback(() => {
    if (!token || !persistentToken) return;

    setRedeemingPersistentToken(true);
    setPersistentTokenErrorMessage("");
    api.persistentTokens.redeem(
      token,
      () => {
        toast.success("Invitation accepted");
        reloadUser(() => {
          handleRedirect(persistentToken);
        });
        setRedeemingPersistentToken(false);
      },
      (err) => {
        setRedeemingPersistentToken(false);
        setPersistentTokenErrorMessage(err.message);
      },
    );
  }, [persistentToken, reloadUser, token, handleRedirect]);

  return (
    <div style={{ paddingBottom: 5 }}>
      {persistentTokenErrorMessage && (
        <>
          <h1>{t("generic.invitations")}</h1>
          <Divider />
        </>
      )}

      {persistentToken ?
        <>
          <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>
              {t("generic.joinType", { type: getEntityName(persistentToken) })}
            </Header.Content>
          </Header>
          <p>
            {persistentToken.ownerUser ?
              <UserChip user={persistentToken.ownerUser} compact="very" />
            : t("generic.someone")}
            {` ${t("persistentToken.generic.has")} `}
            {getEntityAction(persistentToken)}
            {` ${t("persistentToken.generic.youTo")} `}
            {getForTypeText(persistentToken)}
          </p>
        </>
      : null}

      {persistentTokenErrorMessage ?
        <>
          <Message error>
            <Message.Content>
              {persistentTokenErrorMessage}.{" "}
              {t("persistentToken.linkInvalidMessage")}
            </Message.Content>
          </Message>
          <Divider />
          <p>{t("persistentToken.error.loginMessage")}</p>
          <Button primary as={Link} to={`/login`} style={{ marginBottom: 5 }}>
            {t("entry.login.loginOrSignUp")}
          </Button>
        </>
      : null}

      {persistentTokenLoading ?
        <Loader active inline="centered" />
      : null}

      {persistentToken ?
        user ?
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 35,
              marginTop: 20,
              width: "100%",
            }}
          >
            <Button
              primary
              fluid
              icon="check"
              loading={redeemingPersistentToken}
              onClick={redeemPersistentToken}
              style={{ maxWidth: 500 }}
              disabled={!!persistentTokenErrorMessage}
            >
              {t("invitation.accept")}
            </Button>
          </div>
        : <>
            <Message style={{ marginTop: 25 }} info>
              {t("persistentToken.loginToAccept", {
                action: getActionText(persistentToken),
              })}
            </Message>
            <Button
              style={{ float: "right" }}
              as={Link}
              content={t("entry.login.loginOrSignUp")}
              primary
              to={`/login?then=/join/${token}`}
            />
          </>

      : null}
    </div>
  );
};

export default PersistentToken;
